import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, LayoutDashboard, Calendar, AlertCircle } from "lucide-react";
import BloqLogo from "@/components/BloqLogo";
import api from "@/lib/api";

/**
 * MeetingUnavailable — landing page for any attempt to enter an
 * already-ended meeting via /m/{publicId} or /m/{publicId}/room.
 *
 *   • Guest visitor → Create account / Sign in CTA
 *   • Logged-in user → Go to dashboard / View meetings
 *
 * The page never shows the live room; the meeting status is fetched
 * server-side and the body is rendered based on it. If the meeting still
 * exists but isn't ended, we bounce the user back to the lobby (this
 * page is only reachable when the lobby itself redirects here).
 */
export default function MeetingUnavailable() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = (() => {
    try { return !!localStorage.getItem("bloq_token"); } catch { return false; }
  })();

  useEffect(() => {
    if (!publicId) { setLoading(false); return; }
    api.get(`/meetings/public/${publicId}`)
      .then(({ data }) => {
        setMeeting(data);
        // If the meeting is somehow still live, send the user to the
        // lobby — this page is only for ended meetings.
        const status = (data?.status || "").toLowerCase();
        const isEnded = ["ended", "completed", "canceled", "cancelled"].includes(status);
        if (!isEnded) {
          navigate(`/m/${publicId}`, { replace: true });
        }
      })
      .catch(() => setMeeting(null))
      .finally(() => setLoading(false));
  }, [publicId, navigate]);

  if (loading) return null;
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background:
          "radial-gradient(900px 600px at 100% 0%, rgba(0,94,122,0.18), transparent 60%)," +
          "#f5efe6",
      }}
      data-testid="meeting-unavailable-page"
    >
      <header className="px-6 py-5">
        <BloqLogo size={26} />
      </header>
      <main className="flex-1 grid place-items-center px-6 py-12">
        <div
          className="max-w-xl w-full bg-white shadow-xl border border-[#e4e0d8] p-10"
          style={{ borderRadius: 0 }}
        >
          <div className="flex items-center gap-3 mb-2 text-[#005e7a]">
            <AlertCircle className="w-5 h-5" />
            <p className="bloq-eyebrow">Meeting unavailable</p>
          </div>
          <h1 className="bloq-headline text-3xl sm:text-4xl leading-tight mt-2">
            This meeting is not available right now.
          </h1>
          {meeting?.title && (
            <p className="text-sm text-[#82828c] mt-3">
              "{meeting.title}" has ended. The host can share the recap once it's
              ready.
            </p>
          )}
          <div className="flex flex-wrap gap-3 mt-8">
            {isLoggedIn ? (
              <>
                <Link
                  to="/app"
                  className="bloq-btn-primary inline-flex items-center gap-2"
                  data-testid="unavailable-goto-dashboard"
                  style={{ borderRadius: 0 }}
                >
                  <LayoutDashboard className="w-4 h-4" /> Go to dashboard
                </Link>
                <Link
                  to="/app/meetings"
                  className="bloq-btn-secondary inline-flex items-center gap-2"
                  data-testid="unavailable-view-meetings"
                  style={{ borderRadius: 0 }}
                >
                  <Calendar className="w-4 h-4" /> View meetings
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/beta-disclaimer"
                  className="bloq-btn-primary inline-flex items-center gap-2"
                  data-testid="unavailable-create-account"
                  style={{ borderRadius: 0 }}
                >
                  Create account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/login"
                  className="bloq-btn-secondary"
                  data-testid="unavailable-sign-in"
                  style={{ borderRadius: 0 }}
                >
                  Sign in
                </Link>
              </>
            )}
          </div>
          <p className="text-[11px] text-[#82828c] mt-7 leading-snug">
            If you believe this is a mistake, contact the meeting host.
          </p>
        </div>
      </main>
      <footer className="px-6 py-5 text-xs text-[#82828c]">
        Powered by Bloq · bloq.video
      </footer>
    </div>
  );
}
